// libs
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import { TAG_IDENTIFIER, ERROR_TAGS } from 'shared/helpers/SentryLogger/constants';

// helpers
import Storage from 'shared/helpers/storage';
import { log } from 'shared/helpers/newRelic';

//***Image Slide Tracking***

let imgSwipedCache: { [key: string]: number } = {};
const LOCAL_STORAGE_KEY = 'PLP_IMAGE_SWIPED';
const DEBOUCE_TIMESPTAMP = 2000;

const setStorage = (dataToStore: typeof imgSwipedCache) => {
  Storage.setItem(LOCAL_STORAGE_KEY, dataToStore);
  imgSwipedCache = {};
};

/**
 *
 * This method is responsible for dumping the data in localstorage,
 * method is debounced for 200ms on the basis of inputs
 *
 * @param {string} data - include the stringified data for cached result of 'imgSwipedCache'
 *
 */
const dumpAnalyticsData = debounce((data: string) => {
  try {
    const analyticsIndexMap = JSON.parse(data);
    const prevSession = Storage.getItem<typeof imgSwipedCache>(LOCAL_STORAGE_KEY);

    if (isEmpty(analyticsIndexMap)) {
      return;
    }

    // if local storage is empty, we can directly push the data
    if (prevSession === null || isEmpty(prevSession)) {
      setStorage(analyticsIndexMap);
      return;
    }

    // before launch layer clears the data in localstorage, we need to always update the existing data
    const dataToStore = { ...prevSession, ...analyticsIndexMap };
    Object.keys(dataToStore).forEach((key) => {
      dataToStore[key] = Math.max(analyticsIndexMap[key] || 0, prevSession[key] || 0);
    });

    setStorage(dataToStore);
  } catch (err) {
    // report the failure case in sentry
    log({
      errTitle: 'Error: impressions - Data in local stoage was not captured',
      tags: { [TAG_IDENTIFIER.MODULE]: ERROR_TAGS.PLP_IMAGE_SWIPED },
      extraInfo: {
        data,
        localStorageKey: LOCAL_STORAGE_KEY,
      },
    });
  }
}, DEBOUCE_TIMESPTAMP);

/**
 * this method is responsible for:
 * 1) memoizing the last swiped image in global variable 'imgSwipedCache'
 * 2) after 2000 ms, data should get dump to localstorage
 *
 * @param {string} productId = the product swiped on plp page
 * @param {number} currentIndex = the last index of the image swiped
 *
 */
export const memoImageSwipe = (productId: string, currentIndex: number) => {
  if (currentIndex === 0) {
    return;
  }

  // return if product is swiped backward
  if (imgSwipedCache[productId] && imgSwipedCache[productId] > currentIndex) {
    return;
  }

  // memo last index swiped on for a product on plp page
  imgSwipedCache[productId] = currentIndex;
  // stringified so that debounce would be able to compare the data
  dumpAnalyticsData(JSON.stringify(imgSwipedCache));
};
